import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { KollelFormData } from '../schemas/kollelFormSchema';
import Input from '@/components/ui/Input';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { KollelStipendSettingsDtoIn } from '@/models/in/KollelDtoIn';

interface KollelStipendStepProps {
  fundId?: number;
}

/**
 * Step 4 of 5 — "מלגות אברכים" (HAT-1446 phase 4): the kollel's own per-avrech stipend sub-caps.
 * These are the coordinator's own numbers, not an office policy - the one real policy figure (the
 * total ceiling, Figma's "סכום זכאות מוגבל ל-3,500 ₪") is fetched from the server and is normally
 * unset (null) until the office approves it, in which case this step accepts any total and simply
 * shows the running sum without a limit.
 */
const KollelStipendStep = ({ fundId }: KollelStipendStepProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<KollelFormData>();

  const { data: stipendSettings } = useGetAuthApi<KollelStipendSettingsDtoIn>(
    fundId ? `funds/${fundId}/kollels/stipend-settings` : '',
  );
  const maxTotal = stipendSettings?.maxTotalStipendPerAvrech ?? null;

  const excellenceLimit = watch('excellenceLimit');
  const examsLimit = watch('examsLimit');
  const punctualityLimit = watch('punctualityLimit');
  const total = (excellenceLimit ?? 0) + (examsLimit ?? 0) + (punctualityLimit ?? 0);
  const overCap = maxTotal != null && total > maxTotal;

  const fields = [
    ['excellenceLimit', 'excellenceLimitLabel', excellenceLimit] as const,
    ['examsLimit', 'examsLimitLabel', examsLimit] as const,
    ['punctualityLimit', 'punctualityLimitLabel', punctualityLimit] as const,
  ];

  return (
    <div className="w-full flex flex-col gap-4">
      <h3 className="heading-h3-semibold text-(--font-primary)">{t('kollels.wizard.step4.title')}</h3>
      <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
        {fields.map(([field, labelKey, value]) => (
          <label key={field} className="flex flex-col gap-1">
            <span className="body-sm-semibold text-(--font-secondary)">{t(`kollels.form.${labelKey}`)}</span>
            <Input
              type="number"
              min={0}
              value={value ?? ''}
              onChange={(e) => setValue(field, e.target.value === '' ? null : Number(e.target.value))}
            />
          </label>
        ))}
      </div>
      <p className={`body-sm-regular ${overCap ? 'text-red-500' : 'text-(--font-secondary)'}`}>
        {maxTotal != null
          ? t('kollels.form.stipendTotalWithCap', { total, max: maxTotal })
          : t('kollels.form.stipendTotalNoCap', { total })}
      </p>
    </div>
  );
};

export default KollelStipendStep;
